import type { StageAssignmentDtoSlotEnumKey } from "@synthesis/shared-services/internal/application/types";

import type { AssignEmployeeAction } from "./application-control-employee-modal.lib";
import type { ApplicationUiAction, TransitionAction } from "./application-detail-controls.lib";

export type TransitionCommentPopupAction = {
  type: "transition-comment";
  requestId: string;
  action: TransitionAction;
  title: string;
  description: string;
  submitLabel: string;
  commentPlaceholder: string;
};

/**
 * Целевое API действия назначения сотрудника:
 * - transition — первичное назначение через переход POST /transitions/{action} с assigneeId;
 * - reassign — переназначение занятого слота через PUT /assignments/{slot} (статус не меняется).
 */
export type AssignEmployeeTarget =
  | { kind: "transition"; transitionAction: TransitionAction }
  | { kind: "reassign"; slot: StageAssignmentDtoSlotEnumKey };

export type AssignEmployeePopupAction = {
  type: "assign-employee";
  requestId: string;
  action: AssignEmployeeAction;
  target: AssignEmployeeTarget;
};

export type RejectApplicationPopupAction = {
  type: "reject-application";
  requestId: string;
};

export type AssignRegionDepartmentsPopupAction = {
  type: "assign-region-departments";
  requestId: string;
};

export type IssueInvoicePopupAction = {
  type: "issue-invoice";
  requestId: string;
};

export type PaymentProofPopupAction = {
  type: "payment-proof";
  requestId: string;
  isReplacement: boolean;
};

export type UploadReviewDocumentPopupAction = {
  type: "upload-review-document";
  requestId: string;
};

export type UploadTuPartPopupAction = {
  type: "upload-tu-part";
  requestId: string;
  departmentId: string;
};

export type ApplicationControlPopupAction =
  | { type: "import-application" }
  | TransitionCommentPopupAction
  | AssignEmployeePopupAction
  | RejectApplicationPopupAction
  | AssignRegionDepartmentsPopupAction
  | IssueInvoicePopupAction
  | PaymentProofPopupAction
  | UploadReviewDocumentPopupAction
  | UploadTuPartPopupAction;

const COMMENT_ACTION_CONFIG: Partial<
  Record<TransitionAction, Pick<TransitionCommentPopupAction, "title" | "description" | "submitLabel" | "commentPlaceholder">>
> = {
  "request-revision": {
    title: "Вернуть на доработку",
    description: "Укажите замечания для заявителя. Комментарий будет сохранён вместе с заявкой.",
    submitLabel: "Вернуть на доработку",
    commentPlaceholder: "Опишите, что нужно доработать",
  },
  "ntp-rework": {
    title: "Вернуть решение на доработку",
    description: "Укажите замечания по итогам согласования НТП.",
    submitLabel: "Вернуть на доработку",
    commentPlaceholder: "Замечания к решению",
  },
  "ng-rework": {
    title: "Вернуть решение на доработку",
    description: "Укажите замечания по итогам согласования НГ.",
    submitLabel: "Вернуть на доработку",
    commentPlaceholder: "Замечания к решению",
  },
  "ng-approve": {
    title: "Согласовать и выдать ТУ",
    description: "Прикрепите файл ТУ и при необходимости добавьте комментарий.",
    submitLabel: "Согласовать",
    commentPlaceholder: "Комментарий (необязательно)",
  },
};

const EMPLOYEE_ACTION_CONFIG: Record<AssignEmployeeAction, AssignEmployeeTarget> = {
  "intake-pass": { kind: "transition", transitionAction: "intake-pass" },
  "redirect-ng": { kind: "reassign", slot: "NG_STAFF" },
  "send-to-ntp": { kind: "transition", transitionAction: "send-to-ntp" },
  "redirect-ntp": { kind: "reassign", slot: "NTP_HEAD" },
  "assign-ntp-resp": { kind: "transition", transitionAction: "assign-ntp-resp" },
  "reassign-ntp-responsible": { kind: "reassign", slot: "NTP_RESPONSIBLE" },
};

export const TRANSITION_SNACK_MESSAGES: Record<TransitionAction, { pending: string; fulfilled: string; rejected: string }> = {
  "intake-pass": {
    pending: "Направление в аппарат НГ",
    fulfilled: "Заявка направлена в аппарат НГ",
    rejected: "Не удалось направить заявку в аппарат НГ",
  },
  "request-revision": {
    pending: "Возврат на доработку",
    fulfilled: "Заявка возвращена на доработку",
    rejected: "Не удалось вернуть заявку на доработку",
  },
  reject: {
    pending: "Выполняется оформление отказа...",
    fulfilled: "Отказ оформлен и направлен заявителю. Рассмотрение заявки завершено.",
    rejected: "Не удалось оформить отказ. Попробуйте еще раз",
  },
  "take-ng": {
    pending: "Взятие заявки в работу",
    fulfilled: "Заявка взята в работу",
    rejected: "Не удалось взять заявку в работу",
  },
  "send-to-ntp": {
    pending: "Направление в НТП",
    fulfilled: "Заявка направлена в НТП",
    rejected: "Не удалось направить заявку в НТП",
  },
  "take-ntp": {
    pending: "Взятие заявки в работу",
    fulfilled: "Заявка взята в работу",
    rejected: "Не удалось взять заявку в работу",
  },
  "assign-ntp-resp": {
    pending: "Назначение ответственного НТП",
    fulfilled: "Ответственный НТП назначен",
    rejected: "Не удалось назначить ответственного НТП",
  },
  "start-commission-prep": {
    pending: "Взятие заявки в работу",
    fulfilled: "Начата подготовка комиссии",
    rejected: "Не удалось взять заявку в работу",
  },
  "start-decision": {
    pending: "Переход к формированию решения",
    fulfilled: "Начато формирование решения",
    rejected: "Не удалось перейти к формированию решения",
  },
  "submit-ntp-approval": {
    pending: "Выполняется отправка на согласование...",
    fulfilled: "Итоговое решение отправлено на согласование",
    rejected: "Не удалось отправить итоговое решение. Попробуйте еще раз",
  },
  "ntp-rework": {
    pending: "Возврат решения на доработку",
    fulfilled: "Решение возвращено на доработку",
    rejected: "Не удалось вернуть решение на доработку",
  },
  "ntp-approve": {
    pending: "Согласование НТП",
    fulfilled: "Решение согласовано НТП",
    rejected: "Не удалось согласовать решение",
  },
  "ng-rework": {
    pending: "Возврат решения на доработку",
    fulfilled: "Решение возвращено на доработку",
    rejected: "Не удалось вернуть решение на доработку",
  },
  "ng-approve": {
    pending: "Выдача ТУ",
    fulfilled: "ТУ выдано",
    rejected: "Не удалось выдать ТУ",
  },
  "rework-resubmit": {
    pending: "Выполняется отправка на согласование...",
    fulfilled: "Итоговое решение отправлено на согласование",
    rejected: "Не удалось отправить итоговое решение. Попробуйте еще раз",
  },
};

const isEmployeeAction = (action: ApplicationUiAction): action is AssignEmployeeAction => action in EMPLOYEE_ACTION_CONFIG;

const isCommentTransitionAction = (action: ApplicationUiAction): action is TransitionAction => action in COMMENT_ACTION_CONFIG;

export const buildControlPopupAction = (action: ApplicationUiAction, requestId: string): ApplicationControlPopupAction | null => {
  if (action === "reject") {
    return {
      type: "reject-application",
      requestId,
    };
  }

  if (action === "assign-region-responsible") {
    return {
      type: "assign-region-departments",
      requestId,
    };
  }

  if (isEmployeeAction(action)) {
    return {
      type: "assign-employee",
      requestId,
      action,
      target: EMPLOYEE_ACTION_CONFIG[action],
    };
  }

  if (isCommentTransitionAction(action)) {
    const config = COMMENT_ACTION_CONFIG[action];

    if (!config) {
      return null;
    }

    return {
      type: "transition-comment",
      requestId,
      action,
      ...config,
    };
  }

  return null;
};
